import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from "@/lib/permissions";
import { User, Buildings, EnvelopeSimple, ShieldCheck, LockKey, CircleNotch } from "@phosphor-icons/react";

export default function Settings() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const rows = [
    { icon: User, label: "Fullständigt namn", value: user?.name },
    { icon: Buildings, label: "Företag", value: user?.company_name || "—" },
    { icon: EnvelopeSimple, label: "E-post", value: user?.email },
    { icon: ShieldCheck, label: "Roll", value: ROLE_LABELS[user?.role] || user?.role },
  ];

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Lösenordet måste vara minst 8 tecken.");
    if (pw !== confirm) return toast.error("Lösenorden matchar inte.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Lösenordet uppdaterat");
      setCur(""); setPw(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Misslyckades");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none";

  return (
    <div className="p-6 sm:p-8 max-w-3xl">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Inställningar</h1>
        <p className="mt-1 text-zinc-500">Detaljer om ditt konto och din arbetsyta.</p>
      </div>

      <div className="mt-8 bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-zinc-200 flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-zinc-900 flex items-center justify-center text-white">
            <User size={26} weight="bold" />
          </div>
          <div>
            <div className="font-display font-bold text-xl" data-testid="settings-user-name">{user?.name}</div>
            <div className="text-sm text-zinc-500">{user?.email}</div>
          </div>
        </div>
        <div className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <div key={r.label} className="px-6 py-4 flex items-center gap-4">
              <r.icon size={20} weight="bold" className="text-[#0033FF]" />
              <span className="text-sm font-semibold text-zinc-500 w-32">{r.label}</span>
              <span className="text-sm text-zinc-900">{r.value}</span>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 bg-zinc-50 text-xs text-zinc-500">{ROLE_DESCRIPTIONS[user?.role]}</div>
      </div>

      {/* Change password */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-lg p-6">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><LockKey size={20} weight="bold" className="text-[#0033FF]" /> Byt lösenord</h3>
        <form onSubmit={changePassword} className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="settings-change-password-form">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Nuvarande</label>
            <input data-testid="settings-current-pw" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} placeholder="••••••••" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Nytt</label>
            <input data-testid="settings-new-pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} placeholder="••••••••" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Bekräfta</label>
            <input data-testid="settings-confirm-pw" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="••••••••" />
          </div>
          <div className="sm:col-span-3">
            <button type="submit" disabled={loading} data-testid="settings-save-password"
              className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2 text-sm font-semibold transition-colors disabled:opacity-60">
              {loading && <CircleNotch size={16} className="animate-spin" />} Uppdatera lösenord
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 bg-white border border-zinc-200 rounded-lg p-6">
        <h3 className="font-display font-bold text-lg">Arbetsyteplan</h3>
        <p className="text-sm text-zinc-500 mt-1">Du har <strong className="text-zinc-900">Starter</strong>-planen med obegränsat antal agenter under förhandsvisningen.</p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0033FF]/5 border border-[#0033FF]/15 px-4 py-2 text-sm font-semibold text-[#0033FF]">
          ✦ Premiumplattform för AI-kundtjänst
        </div>
      </div>
    </div>
  );
}
